import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  input,
  linkedSignal,
  output,
  signal,
  viewChild
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  BookingReferenceHttpService
} from '../../location/booking-entity-overview/booking-reference-http.service';
import {formatInvariantDecimal, parseInvariantDecimal} from '../../../helpers/decimal-input-helper';
// One reader for "where does the code live on a failed response", shared with the auth surface rather than
// re-implemented here.
import {readErrorCode} from '../../../_auth/two-factor/second-factor-errors';

/** The message shown for anything `parseInvariantDecimal` refuses (letters, a negative, two separators, …). */
export const INVALID_LIMIT_MESSAGE = 'Bitte eine gültige, nicht-negative Zahl eingeben (z. B. 12,50).';

/**
 * The fallback for a rejection this dialog has no sentence for.
 *
 * Deliberately generic, and deliberately not a fallback to the server's `ApiError.description`: that field is
 * developer-facing English from `ErrorCatalog` (frontend style guide, *"Surfacing an API error — branch on
 * `code`, never render `description`"*), and a screen that ever falls back to it leaks English the first time
 * the backend ships a code the SPA has not met. If you land here from a real refusal, add its mapping to
 * {@link SAVE_ERROR_MESSAGES} — do not make this string more specific.
 *
 * It is not a rare path either: a deleted reference is a **bare** 404 and an empty id a bare 400, neither
 * carrying an `ApiError` at all.
 */
export const GENERIC_SAVE_ERROR = 'Das Ausgabenlimit konnte nicht gespeichert werden.';

/**
 * `ApiError.code` → German sentence for the refusals this one endpoint can produce.
 *
 * Exactly one, and that is not an oversight: `SetSpendingLimit` calls neither the open/claim path nor the
 * close path, so of `ReferenceLifecycleErrorCodes` only `NotOpen` is reachable — the rest
 * (`E_REFERENCE_ALREADY_OPEN`, `E_REFERENCE_CLAIM_CONTENDED`, `E_REFERENCE_LIMIT_EXCEEDED`,
 * `E_REFERENCE_CLOSE_BALANCE_OPEN`, `E_REFERENCE_TAKEN`) are register-side floor work, and the catalog's own
 * class doc says as much.
 */
const SAVE_ERROR_MESSAGES: Readonly<Record<string, string>> = {
  // "It was never opened, or somebody (or the close-on-settle policy) closed it first." On this screen that is
  // always the second reading — the dialog opened from a card that WAS open — so the sentence says which.
  E_REFERENCE_NOT_OPEN:
    'Dieser Tisch ist nicht mehr geöffnet — er wurde zwischenzeitlich geschlossen oder bezahlt. Ein ' +
    'Ausgabenlimit lässt sich nur für einen offenen Tisch setzen.'
};

/** Keeps the `id`s of two dialogs (a stale one animating out, a story rendering several) apart. */
let dialogCounter = 0;

/**
 * "Ausgabenlimit ändern" — the web app's one write on an open booking reference (#204).
 *
 * Reached from a card's kebab menu in the Offene-Tische overview, and gated **by the host** to
 * `ShardAdmin`/`CompanyOwner`/`PlatformOwner` (`Operator`, the read-only baseline role that also reaches that
 * screen, must not see the entry point at all). This is the web half of a deliberately asymmetric lifecycle:
 * there is no *close* action here — closing a table is register-only floor work. See
 * `v2025_4.ReferenceController.SetSpendingLimit`'s own remarks for the rationale.
 *
 * **It owns the save, not the outcome.** The HTTP call, the validation and the error text live here; applying the
 * new value to the live rows and telling the user it worked belong to the host, which owns the store. The
 * component therefore emits {@link saved} with the value that reached the wire.
 *
 * **Bespoke chrome on purpose.** The rest of the app would reach for `layout-confirm-popup`, but this screen is a
 * near-literal design handover that uses none of the app's utility scale (see `OpenTablesComponent`), and the
 * shared popup carries neither `role="dialog"` nor a focus contract. Drawing it here keeps the screen coherent
 * *and* lets the dialog be a real modal for assistive tech.
 */
@Component({
  selector: 'app-spending-limit-dialog',
  standalone: true,
  imports: [],
  host: {'(keydown.escape)': 'cancelled.emit()'},
  styles: [`
    :host {
      position: absolute;
      inset: 0;
      z-index: 40;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      font-family: 'Rubik', system-ui, sans-serif;
      line-height: normal;
      color: #1c1c1e;
    }

    .sld-backdrop {
      position: absolute;
      inset: 0;
      background: rgba(28, 28, 30, .45);
    }

    .sld-panel {
      position: relative;
      width: 100%;
      max-width: 420px;
      background: #fff;
      border-radius: 16px;
      padding: 20px;
      box-shadow: 0 24px 60px -20px rgba(0, 0, 0, .5);
      display: flex;
      flex-direction: column;
      gap: 14px;
    }

    .sld-title {
      margin: 0;
      font-size: 17px;
      font-weight: 700;
    }

    .sld-label {
      display: block;
      font-size: 13px;
      font-weight: 600;
      color: #6b6b70;
      margin-bottom: 6px;
    }

    .sld-input {
      width: 100%;
      font: inherit;
      font-size: 15px;
      padding: 10px 12px;
      border-radius: 10px;
      border: 1.5px solid #e6e4e0;
      background: #fff;
      color: #1c1c1e;
    }

    .sld-input:focus {
      outline: 2px solid #c50e6c;
      outline-offset: 1px;
      border-color: #c50e6c;
    }

    .sld-input[aria-invalid='true'] {
      border-color: #c0392b;
    }

    .sld-input[aria-invalid='true']:focus {
      outline-color: #c0392b;
    }

    /* #c0392b on white is 5.0:1 — the same Alert red the escalation levels use, and clear of WCAG AA. */
    .sld-error {
      margin: 8px 0 0;
      font-size: 12.5px;
      font-weight: 600;
      color: #c0392b;
    }

    .sld-hint {
      margin: 8px 0 0;
      font-size: 12.5px;
      color: #6b6b70;
    }

    .sld-actions {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      flex-wrap: wrap;
    }

    .sld-btn {
      font: inherit;
      font-size: 13.5px;
      font-weight: 600;
      padding: 9px 16px;
      border-radius: 10px;
      border: 1.5px solid transparent;
      cursor: pointer;
    }

    .sld-btn:disabled {
      cursor: default;
      opacity: .6;
    }

    .sld-btn-ghost {
      background: #fff;
      border-color: #e6e4e0;
      color: #1c1c1e;
    }

    .sld-btn-ghost:hover:not(:disabled) {
      background: #f5f4f2;
    }

    /* #c50e6c carries white text at 8.4:1. */
    .sld-btn-primary {
      background: #c50e6c;
      color: #fff;
    }

    .sld-btn-primary:hover:not(:disabled) {
      background: #a80b5c;
    }
  `],
  template: `
    <div class="sld-backdrop" aria-hidden="true" (click)="cancelled.emit()"></div>
    <div class="sld-panel" role="dialog" aria-modal="true" [attr.aria-labelledby]="titleId">
      <h2 class="sld-title" [id]="titleId">Ausgabenlimit ändern — {{ tableName() }}</h2>

      <div>
        <label class="sld-label" [attr.for]="inputId">Limit in € (leer = kein Limit)</label>
        <!--
          Deliberately type="text" + inputmode="decimal". A native type="number" delegates comma/dot handling to
          the browser's own locale, which is untestable in CI and is exactly how a "0,4" once reached a backend
          as 4. parseInvariantDecimal is the single place a locale may leak in, and it accepts both separators
          — so a German admin's "12,50" always reaches the wire as the invariant number 12.5.
        -->
        <input #box class="sld-input" type="text" inputmode="decimal" [id]="inputId" placeholder="0,00"
               [value]="limitInput()" (input)="limitInput.set($any($event.target).value)"
               [attr.aria-invalid]="invalid()" [attr.aria-describedby]="invalid() ? errorId : hintId"/>
        @if (invalid()) {
          <p class="sld-error" [id]="errorId">{{ INVALID_LIMIT_MESSAGE }}</p>
        } @else {
          <p class="sld-hint" [id]="hintId">Komma oder Punkt als Dezimaltrennzeichen.</p>
        }
      </div>

      @if (error(); as message) {
        <p class="sld-error" role="alert">{{ message }}</p>
      }

      <div class="sld-actions">
        <button type="button" class="sld-btn sld-btn-ghost" (click)="cancelled.emit()">Abbrechen</button>
        <!--
          Deliberately NOT disabled while the input is invalid. A disabled submit is silent: it announces
          nothing, cannot be focused, and leaves a keyboard user pressing a dead control with no idea why. The
          press is allowed, blocked in save(), and answered by the message + aria-invalid on the box.
        -->
        <button type="button" class="sld-btn sld-btn-primary" [disabled]="saving()"
                (click)="save()">{{ saving() ? 'Speichern …' : 'Speichern' }}
        </button>
      </div>
    </div>
  `
})
export class SpendingLimitDialogComponent {
  /** The booking reference to write to — the card's own id. */
  readonly referenceId = input.required<string>();
  /** What the table is called, for the dialog's title. */
  readonly tableName = input('');
  /** The limit currently in force, in euros, or `null` for none. Seeds the box. */
  readonly currentLimit = input<number | null>(null);

  /** The limit that reached the wire — euros, or `null` when it was cleared. */
  readonly saved = output<number | null>();
  readonly cancelled = output<void>();

  private readonly http = inject(BookingReferenceHttpService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly box = viewChild.required<ElementRef<HTMLInputElement>>('box');

  protected readonly titleId = `sld-title-${++dialogCounter}`;
  protected readonly inputId = `sld-input-${dialogCounter}`;
  protected readonly errorId = `sld-error-${dialogCounter}`;
  protected readonly hintId = `sld-hint-${dialogCounter}`;

  protected readonly INVALID_LIMIT_MESSAGE = INVALID_LIMIT_MESSAGE;

  /**
   * The typed text.
   *
   * A `linkedSignal` rather than a plain one so it re-seeds if the host ever swaps the dialog's target without
   * destroying it, while still being freely writable as the user types. Formatted invariantly (`12.5`, never a
   * localized `12,50`) — the box accepts either separator on the way back in.
   */
  protected readonly limitInput = linkedSignal(() => formatInvariantDecimal(this.currentLimit()));

  protected readonly saving = signal(false);
  /** The backend's own refusal, kept visible so the admin can read it and retry rather than guess. */
  protected readonly error = signal('');

  /** Whether the typed text fails to parse. An empty box is **valid** — it means "clear the limit". */
  protected readonly invalid = computed(() => {
    const text = this.limitInput().trim();
    return text !== '' && parseInvariantDecimal(text) === null;
  });

  constructor() {
    // A modal that opens without moving focus leaves a keyboard user tabbing through the page behind it.
    afterNextRender(() => this.box().nativeElement.focus(), {injector: this.injector});
  }

  /**
   * Sends the typed limit.
   *
   * An empty box clears the limit (`null`). `DuplicateRequestInterceptor` collapses byte-identical *concurrent*
   * resends of this exact `{method, url, body}`, which is precisely right here: a double-click must apply once.
   */
  protected save(): void {
    if (this.saving()) return;
    if (this.invalid()) {
      // The message is already on screen and already tied to the box through `aria-describedby`; putting focus
      // back on the box is what turns a rejected press into something a screen reader actually reports.
      this.box().nativeElement.focus();
      return;
    }

    const limit = parseInvariantDecimal(this.limitInput());
    this.saving.set(true);
    this.error.set('');

    this.http.setSpendingLimit(this.referenceId(), limit)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.saved.emit(limit);
        },
        error: (err: unknown) => {
          this.saving.set(false);
          // German mapped from the structured error's CODE, never its `description`. The web app realistically
          // only ever sees E_REFERENCE_NOT_OPEN here — the reference closed in the meantime.
          this.error.set(describeError(err));
        }
      });
  }
}

/**
 * The German sentence for a failed save.
 *
 * Branches on `ApiError.code`; an unrecognised or absent code becomes {@link GENERIC_SAVE_ERROR}. It used to
 * render the `description` instead, which put *"This booking reference is not open."* on a screen whose every
 * other word — "Offene Tische", "Ausgabenlimit ändern", "Speichern" — is German.
 */
function describeError(err: unknown): string {
  const code = readErrorCode(err);
  return (code && SAVE_ERROR_MESSAGES[code]) || GENERIC_SAVE_ERROR;
}
